import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { Subscription } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { BidManagementStatus, LoggedInUser, LoggedInUserService } from '../../auth/logged.in.user';
import { PermissionType } from '../../auth/permission';
import { Configuration } from '../../configuration';
import { DateConverter } from '../../helpers/date.converter';
import { DocumentsAcknowledgedDto } from '../../models/account/account.models';
import { CmsDialogDto } from '../../models/cms.dialogs/cms.dialogs.models';
import { CmsPageType, GetCmsPageDto } from '../../models/cms.pages/cms.pages.models';
import { IPage, PagingRequest } from '../../models/common/paging';
import { SelectableItem } from '../../models/common/selectableItem';
import { HvtSection1Dto, HvtSection2Dto, HvtSection3Dto, HvtSection4Dto, HvtSection6Dto } from '../../models/notices/ojeu.notice.models';
import { TenderTeamMemberSummaryDto } from '../../models/tender.team.members/tender.team.member.models';
import { AddSubscriberNoticeNoteDto, NoticeDocument, TenderDetailsDto, TenderNote, TenderReminder, TenderSummaryDto } from '../../models/tenders/tender.models';
import { TenderTabCountDto } from '../../models/tenders/tender.tab.count.model';
import { TenderTabInformationDto } from '../../models/tenders/tender.tab.information.model';
import { AccountService } from '../../services/account.service';
import { CmsDialogService } from '../../services/cms.dialog.service';
import { CmsPageService } from '../../services/cms.page.service';
import { TenderService } from '../../services/tender.service';
import { TenderTeamMemberService } from '../../services/tender.team.member.service';
import { PermissionsHub } from '../../signalr/permissions.hub';
import { appInsights } from '../../app.module';

@Component({
    selector: 'tender-results',
    template: require('./results.component.html'),
    styles: [require('./results.component.css')]
})
export class TenderResultsComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly ENGLAND_LOCATIONS = [
        'East England',
        'East Midlands',
        'London',
        'North East',
        'North West',
        'South Coast',
        'South East',
        'South West',
        'West Midlands',
        'Yorkshire & Humber'
    ];

    private static readonly UK_LOCATIONS = [
        ...TenderResultsComponent.ENGLAND_LOCATIONS,
        'Scotland',
        'Wales',
        'Northern Ireland'
    ];

    private _tenders: TenderSummaryDto[];
    tenderSelected = false;
    selectedTender: TenderDetailsDto;
    selectedTenderSummary: TenderSummaryDto;

    pageSizes = [10, 25, 50];

    @BlockUI('tender-details') detailsBlockUi: NgBlockUI;

    @ViewChild('hvtS1', { static: false }) hvtS1Comp: any;
    @ViewChild('hvtS2', { static: false }) hvtS2Comp: any;
    @ViewChild('hvtS3', { static: false }) hvtS3Comp: any;
    @ViewChild('hvtS4', { static: false }) hvtS4Comp: any;
    @ViewChild('hvtS6', { static: false }) hvtS6Comp: any;

    resultsCollapsed = false;
    extrasExpanded = false;
    extrasTab: string;

    tenderToSend: TenderDetailsDto;
    tenderToAssign: TenderSummaryDto;

    addingNote: boolean;
    noteToAdd: string;

    addingReminder: boolean;
    reminderDate = moment();
    reminderTimeHours = this.reminderDate.hours() + 1;
    reminderTimeMinutes = 0;
    reminderText: string;
    showReminderError: boolean;
    hours: Array<SelectableItem<number>>;
    minutes: Array<SelectableItem<number>>;

    teamMembers: Array<TenderTeamMemberSummaryDto>;
    teamMemberId: number;
    isAssignOpen = false;

    @ViewChild('assignContainer', { static: false }) assignContainerRef: ElementRef<HTMLElement>;

    minimumDesktopResolution: number = 1024;

    tabCounts: TenderTabCountDto = new TenderTabCountDto({
        noteCount: 0,
        reminderCount: 0
    });
    reminderDto: TenderTabInformationDto = null;

    container: string;
    documentsAgreed: boolean;
    acknowledgedDate: string;

    dialogHeader: string;
    dialogBody: string;

    cmsAdmin: string;
    cmsPro: string;

    activatedTab = 'overview';
    hasBuyerContentFromList = false;

    private readonly user: LoggedInUser;

    resultsScroll;

    autoRefreshSeconds = 30;
    autoRefreshDelay = this.autoRefreshSeconds * 1000;
    autoRefreshSub: Subscription;
    isBidManager: boolean = false;
    bidManagementUrl: string;

    zipDialogName: string;
    zipDialogHeader: string;
    zipDialogBody: string;

    showReminder: boolean = false;

    @Input()
    options: ITenderResultOptions = {
        availableButtons: []
    };

    @Input()
    set tenders(tenders: TenderSummaryDto[]) {
        this._tenders = tenders;

        if (this._tenders && this._tenders.length > 0) {
            this._tenders.forEach((tender) => tender.selected = false);

            if (this.isDesktopMode()) {
                this.showDetails(this._tenders[0]);
            }
        }

        if (this.resultsScroll && this.resultsScroll.scrollTo) {
            this.resultsScroll.scrollTo(0, 0);
        }
    }

    @Input() pagingRequest: PagingRequest;
    @Input() page: IPage<any>;

    @Input() sortSelectOptions: Array<SelectableItem<number>> = null;
    @Input() sortOptionId: number = null;
    @Input() sortDisabled: boolean = false;
    @Output() sortOptionIdChange = new EventEmitter<number>();

    @Output() onResultRemoved = new EventEmitter<any>();
    @Output() onMultipleResultsRemoved = new EventEmitter<any>();
    @Output() onBookmarkChanged = new EventEmitter<any>();

    constructor(private readonly tenderService: TenderService,
        private readonly loggedInUserService: LoggedInUserService,
        private readonly configuration: Configuration,
        private readonly accountService: AccountService,
        private readonly authService: AuthService,
        private readonly teamMemberService: TenderTeamMemberService,
        private readonly cmsDialogService: CmsDialogService,
        private readonly cmsPageService: CmsPageService) {

        this.user = loggedInUserService.getCurrentUser();
        this.bidManagementUrl = configuration.routing.bidManagementUrl;
        this.selectedTender = null;

        this.hours = [];
        this.minutes = [];

        for (let i = 0; i < 24; i++) {
            this.hours.push({ value: i, text: i < 10 ? `0${i}` : i.toString() });
        }

        for (let i = 0; i < 60; i++) {
            this.minutes.push({ value: i, text: i < 10 ? `0${i}` : i.toString() });
        }

        this.teamMembers = [];

        if (configuration.environmentInfo.environmentName == "Development") {
            this.container = 'development'
        }
        else if (configuration.environmentInfo.environmentName == "UAT") {
            this.container = 'uat'
        }
        else {
            this.container = 'live'
        }

        if (this.canLoadAcknowledgementState()) {
            this.accountService.isAcknowledged().subscribe((response: DocumentsAcknowledgedDto) => {
                this.documentsAgreed = response.documentsAcknowledged;

                if (this.documentsAgreed) {
                    this.acknowledgedDate = response.jsDocumentsAcknowledgedDate;
                }
            });
        }
    }

    onSortSelectionChange(): void {
        if (this.sortDisabled) {
            return;
        }
        this.sortOptionIdChange.emit(+this.sortOptionId);
    }

    ngOnInit() {

        PermissionsHub.startHub(this.authService, this.configuration, this.user.userId);
        PermissionsHub.subscribeToHubEvent('RefreshTenders', (data: any) => {
            if (this.selectedTenderSummary) {
                this.showDetails(this.selectedTenderSummary);
            }
        });

        this.loadTeamMembers();
        this.cmsDialogService.getByDescription('RemoveSelected').subscribe((result: CmsDialogDto) => {
            this.dialogHeader = result.header;
            this.dialogBody = result.body;
        });
        this.cmsPageService.getByType(CmsPageType.DownloadDocsAdmin).subscribe((result: GetCmsPageDto) => {
            this.cmsAdmin = result.content;
        });
        this.cmsPageService.getByType(CmsPageType.DownloadDocsProContracts).subscribe((result: GetCmsPageDto) => {
            this.cmsPro = result.content;
        });
        this.accountService.isBidManager().subscribe((response: boolean) => {
            this.isBidManager = response;
        });

        this.zipDialogName = 'Tender Import Has Zips';

        this.cmsDialogService.getByDescription(this.zipDialogName).subscribe((result: CmsDialogDto) => {
            this.zipDialogHeader = result.header;
            this.zipDialogBody = result.body;
        });
    }

    ngAfterViewInit() {
        this.resultsScroll = document.getElementById('results-scroll');
    }

    ngOnDestroy() {
        this.resultsScroll = null;
        $('#next-modal').modal('hide');
        $('#info-modal').modal('hide');
        $('#check-modal').modal('hide');
        $('#assign-modal').modal('hide');
        if (this.autoRefreshSub) {
            this.autoRefreshSub.unsubscribe();
        }
    }

    private loadTeamMembers() {
        this.teamMemberService.getAll().subscribe((response: Array<TenderTeamMemberSummaryDto>) => {

            this.teamMembers = response;

            var yourself = new TenderTeamMemberSummaryDto();
            yourself.id = this.user.userId;
            yourself.email = "Yourself";
            this.teamMembers.splice(1, 0, yourself);
        });
    }

    get tenders() {
        return this._tenders;
    }

    hasSelectedTenders(): boolean {
        return !!this.tenders && this.tenders.some((tender) => tender.selected);
    }

    isDesktopMode(): boolean {
        return window.innerWidth > this.minimumDesktopResolution;
    }

    showButton(name: string) {
        return this.options.availableButtons.indexOf(name) > -1;
    }

    showDetails(tender: TenderSummaryDto) {
        this.tenderSelected = true;
        this.isAssignOpen = false;
        this.selectedTenderSummary = tender;

        appInsights.trackEvent({
            name: 'TenderSelected',
            properties: {
                tenderId: tender.b2BRef,
                pagePath: window.location.href
            }
        });

        //if (this.selectedTender && this.selectedTender.id === tender.b2BRef) {
        //    return;
        //}

        if (!this.isDesktopMode()) {
            document.querySelector('body').classList.add('fixed-scrolling');
        }

        this.reminderDto = new TenderTabInformationDto();
        this.reminderDto.supplierId = this.user.userId;
        this.reminderDto.noticeId = tender.b2BRef;
        this.tenderService.getTabCount(this.reminderDto)
            .subscribe(x => {
                this.tabCounts = x;
            });

        if (!this.documentsAgreed && this.canLoadAcknowledgementState()) {
            this.accountService.isAcknowledged().subscribe((response: DocumentsAcknowledgedDto) => {
                this.documentsAgreed = response.documentsAcknowledged;

                if (this.documentsAgreed) {
                    this.acknowledgedDate = response.jsDocumentsAcknowledgedDate;
                }
            });
        }        

        this.getTenderDetails(tender);


    }
    hideDetails() {
        this.tenderSelected = false;
        this.selectedTender = null;
        document.querySelector('body').classList.remove('fixed-scrolling');
    }

    toggleCollapseResults() {
        this.resultsCollapsed = !this.resultsCollapsed;
    }

    toggleBidManagement(event, tender: TenderDetailsDto) {
        event.stopPropagation();

        if (tender && tender.sentToBidManagement) {
            this.tenderService.removeFromBidManagement(tender.id).subscribe(() => {
                tender.sentToBidManagement = false;
            });
            return;
        }

        if (this.user.role === 'Supplier') {
            if (this.user.canAdHoc('bidManagementExpired')) {
                $('#expired-bid-manager-modal').modal();
                return;
            }

            if (this.user.canEntity('bidTender', PermissionType.Add)) {
                this.sendTender(tender);
            } else {
                this.tenderToSend = tender;
                $('#start-trial-modal').modal();
            }
        } else {
            if (this.user.teamLeaderBidManagementStatus === BidManagementStatus.Current) {
                this.sendTender(tender);
                return;
            } else if (this.user.teamLeaderBidManagementStatus === BidManagementStatus.Expired) {
                $('#lead-expired-modal').modal();
                return;
            } else {
                $('#lead-start-trial-modal').modal();
                return;
            }
        }
    }

    confirmBidManagementTrial() {
        this.accountService.becomeBidManager().subscribe(() => {
            this.authService.getNewToken().subscribe(() => {
                this.loggedInUserService.refreshUserClaims();
                this.sendTender(this.tenderToSend);
            });
        });
    }

    private sendTender(tender: TenderDetailsDto) {

        this.tenderService.sendToBidManagement(tender.id).subscribe((response: any) => {            
            $('#start-trial-modal').modal('hide');
            if (response.value) {
                $('#zip-info-modal').modal();
            }
            this.tenderToSend = null;
        });

        tender.sentToBidManagement = true;
    }

    removeResult(event, tender: TenderSummaryDto) {
        event.stopPropagation();
        this.onResultRemoved.emit(tender);
    }

    removeMultiple(event) {
        event.stopPropagation();
        var ids = new Array<number>();
        this.tenders.forEach((tender) => {
            if (tender.selected) {
                ids.push(tender.id);
            }
        });

        if (ids.length > 0) {
            this.onMultipleResultsRemoved.emit(ids);
        }        
    }

    clearSelectedIfMatch(tenderId: number) {
        if (tenderId === this.selectedTender.id) {
            this.selectedTender = null;
        }
    }

    bookmark(event, tender: TenderDetailsDto) {
        event.stopPropagation();
        this.tenderService.bookmark(tender.id).subscribe(() => {

            if (tender.bookmarked) {
                tender.bookmarked = false;
                tender.bookmarkedById = 0;
                tender.jsBookmarkedDate = '';
            }
            else {
                tender.bookmarked = true;
                tender.bookmarkedById = this.user.userId;
                tender.jsBookmarkedDate = new Date().toLocaleString();
            }

            this.onBookmarkChanged.emit(tender);
        });
    }

    checkPopup() {
        $('#check-modal').modal();
    }

    assignPopup(event, tender: TenderSummaryDto) {
        this.tenderToAssign = tender;
        if (tender.assignedToId > 0) {
            this.teamMemberId = tender.assignedToId;
        }
        else {
            this.teamMemberId = 0;
        }
        $('#assign-modal').modal();
    }

    assign() {
        //$('#assign-modal').modal('hide');

        this.tenderService.assign(this.selectedTender.id, this.teamMemberId).subscribe(() => {
            this.tenderToAssign.assigned = true;
            this.tenderToAssign.assignedTo = this.teamMembers.find(obj => {
                return obj.id === this.teamMemberId;
            }).name;
        });
    }

    removeAssignment() {
        $('#assign-modal').modal('hide');

        this.tenderService.assign(this.selectedTender.id, 0).subscribe(() => {
            this.tenderToAssign.assigned = false;
            this.tenderToAssign.assignedTo = '';
        });
    }

    private getTenderDetails(tender: TenderSummaryDto) {
        this.detailsBlockUi.start();

        var now = new Date();
        var day = now.getDate() < 10 ? `0${now.getDate()}` : now.getDate().toString();
        var month = now.getMonth() < 9 ? `0${now.getMonth() + 1}` : `${now.getMonth() + 1}`;
        var hour = now.getHours() < 10 ? `0${now.getHours()}` : now.getHours().toString();
        var minutes = now.getMinutes() < 10 ? `0${now.getMinutes()}` : now.getMinutes();

        tender.jsLastViewed = `${day}/${month}/${now.getFullYear()} ${hour}:${minutes}`;

        if (this.user.canAdHoc('tenderDetails')) {
            this.tenderService.getDetails(tender.b2BRef).subscribe((response: TenderDetailsDto) => {
                this.extrasExpanded = false;
                this.activatedTab = 'overview';

                if (this.hvtS1Comp) {
                    this.hvtS1Comp.clear();
                }

                if (this.hvtS2Comp) {
                    this.hvtS2Comp.clear();
                }

                if (this.hvtS3Comp) {
                    this.hvtS3Comp.clear();
                }

                if (this.hvtS4Comp) {
                    this.hvtS4Comp.clear();
                }

                if (this.hvtS6Comp) {
                    this.hvtS6Comp.clear();
                }

                this.selectedTender = response;
                this.hasBuyerContentFromList = false;

                if (this.selectedTender.assignedToId > 0) {
                    this.teamMemberId = this.selectedTender.assignedToId;
                }
                else {
                    this.teamMemberId = 0;
                }

                this.tabCounts.noteCount = this.selectedTender.notes.length;
                this.tabCounts.reminderCount = this.selectedTender.reminders.length;

                this.detailsBlockUi.stop();
            },
                () => { this.detailsBlockUi.stop(); });
        } else {

            this.tenderService.hasDocuments(tender.b2BRef).subscribe((response: boolean) => {

                this.tenderService.getDetails(tender.b2BRef).subscribe((response2: TenderDetailsDto) => {
                    
                    this.selectedTender = new TenderDetailsDto(true);
                    this.selectedTender.id = tender.b2BRef;

                    if (response) {
                        var dummydoc = new NoticeDocument();
                        this.selectedTender.documents = [];
                        this.selectedTender.documents.push(dummydoc);
                    }

                    const hasBuyerContent = !!(tender.buyersDescription && tender.buyersDescription.trim());
                    this.hasBuyerContentFromList = hasBuyerContent;

                    this.selectedTender.b2BTitle = tender.title;
                    this.selectedTender.description = tender.description;
                    if (hasBuyerContent) {
                        this.selectedTender.buyersDescription = this.selectedTender.placeholder;
                    } else {
                        this.selectedTender.buyersDescription = null;
                    }
                    this.selectedTender.type = tender.type;
                    this.selectedTender.ojeuSections = tender.type === 'High' ? [1, 2, 3, 4, 6] : [];

                    if (tender.jsInterestDeadlineDate !== null) {
                        this.selectedTender.jsInterestDeadline = tender.jsInterestDeadlineDate + ' ' + tender.jsInterestDeadlineTime;
                    }

                    if (tender.jsDocumentSubmissionDate !== null) {
                        this.selectedTender.jsDocumentDeadline = tender.jsDocumentSubmissionDate + ' ' + tender.jsDocumentSubmissionTime;
                    }

                    this.selectedTender.value = tender.value;
                    this.selectedTender.sentToBidManagement = tender.sentToBidManagement;

                    if (response2.assignedToId > 0) {
                        this.teamMemberId = response2.assignedToId;
                    }
                    else {
                        this.teamMemberId = 0;
                    }

                    this.selectedTender.sentToBidManagement = response2.sentToBidManagement;

                    this.activatedTab = 'overview';
                    this.detailsBlockUi.stop();
                }, () => { this.detailsBlockUi.stop(); })
            },
                () => { this.detailsBlockUi.stop(); });

            
        }
    }

    showHvtTabs() {
        return this.selectedTender && this.selectedTender.type === 'High' && !this.selectedTender.isSchemaEight;
    }

    hasHvtSection(sectionNumber: number) {
        return this.selectedTender.ojeuSections.some(s => {
            return s === sectionNumber;
        });
    }

    onOpenHvtSection(sectionNumber: number) {
        switch (sectionNumber) {
            case 1:
                {
                    if (!this.selectedTender.hvtSection1) {
                        this.tenderService.getHvtSection(this.selectedTender.id, sectionNumber).subscribe(
                            (response: HvtSection1Dto) => {
                                this.selectedTender.hvtSection1 = response;

                                this.hvtS1Comp.populate(this.selectedTender.hvtSection1,
                                    this.selectedTender.ojeuFormType);
                            });
                    }
                    break;
                }
            case 2:
                {
                    if (!this.selectedTender.hvtSection2) {
                        this.tenderService.getHvtSection(this.selectedTender.id, sectionNumber).subscribe(
                            (response: HvtSection2Dto) => {
                                this.selectedTender.hvtSection2 = response;

                                this.hvtS2Comp.populate(this.selectedTender.hvtSection2,
                                    this.selectedTender.ojeuFormType);
                            });
                    }
                    break;
                }
            case 3:
                {
                    if (!this.selectedTender.hvtSection3) {
                        this.tenderService.getHvtSection(this.selectedTender.id, sectionNumber).subscribe(
                            (response: HvtSection3Dto) => {
                                this.selectedTender.hvtSection3 = response;

                                this.hvtS3Comp.populate(this.selectedTender.hvtSection3,
                                    this.selectedTender.ojeuFormType);
                            });
                    }
                    break;
                }
            case 4:
                {
                    if (!this.selectedTender.hvtSection4) {
                        this.tenderService.getHvtSection(this.selectedTender.id, sectionNumber).subscribe(
                            (response: HvtSection4Dto) => {
                                this.selectedTender.hvtSection4 = response;

                                this.hvtS4Comp.populate(this.selectedTender.hvtSection4,
                                    this.selectedTender.ojeuFormType);
                            });
                    }
                    break;
                }
            case 6:
                {
                    if (!this.selectedTender.hvtSection6) {
                        this.tenderService.getHvtSection(this.selectedTender.id, sectionNumber).subscribe(
                            (response: HvtSection6Dto) => {
                                this.selectedTender.hvtSection6 = response;

                                this.hvtS6Comp.populate(this.selectedTender.hvtSection6,
                                    this.selectedTender.ojeuFormType);
                            });
                    }
                    break;
                }
        }
    }

    onOpenSchema8Section(sectionNumber: number) {
        switch (sectionNumber) {
            case 1:
                {
                    if (!this.selectedTender.schema8Section1) {
                        this.selectedTender.schema8Section1 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section1 = response.value;
                            });
                    }
                    break;
                }
            case 2:
                {
                    if (!this.selectedTender.schema8Section2) {
                        this.selectedTender.schema8Section2 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section2 = response.value;
                            });
                    }
                    break;
                }
            case 3:
                {
                    if (!this.selectedTender.schema8Section3) {
                        this.selectedTender.schema8Section3 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section3 = response.value;
                            });
                    }
                    break;
                }
            case 4:
                {
                    if (!this.selectedTender.schema8Section4) {
                        this.selectedTender.schema8Section4 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section4 = response.value;
                            });
                    }
                    break;
                }
            case 5:
                {
                    if (!this.selectedTender.schema8Section5) {
                        this.selectedTender.schema8Section5 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section5 = response.value;
                            });
                    }
                    break;
                }
            case 6:
                {
                    if (!this.selectedTender.schema8Section6) {
                        this.selectedTender.schema8Section6 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section6 = response.value;
                            });
                    }
                    break;
                }
            case 7:
                {
                    if (!this.selectedTender.schema8Section7) {
                        this.selectedTender.schema8Section7 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section7 = response.value;
                            });
                    }
                    break;
                }
            case 8:
                {
                    if (!this.selectedTender.schema8Section8) {
                        this.selectedTender.schema8Section8 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section8 = response.value;
                            });
                    }
                    break;
                }
            case 9:
                {
                    if (!this.selectedTender.schema8Section9) {
                        this.selectedTender.schema8Section9 = '';
                        this.tenderService.getSchema8Section(this.selectedTender.id, sectionNumber).subscribe(
                            (response: any) => {
                                this.selectedTender.schema8Section9 = response.value;
                            });
                    }
                    break;
                }
        }
    }

    splitMultiLine(obj: string, lineBreak: string = '<br/>') {
        if (obj) {
            return obj.split(lineBreak);
        }
        return [];
    }

    split2(obj: string, lineBreak: string = '<br/>') {
        if (obj) {
            return obj.split(lineBreak);
        }
        return [];
    }

    displayOrNotProvided(value: string) {
        if (value === null || value === undefined) {
            return 'not provided';
        }

        const text = `${value}`.trim();
        return text.length > 0 ? text : 'not provided';
    }

    getDisplaySummaryText(tender: TenderSummaryDto): string {
        if (!tender) {
            return 'not provided';
        }

        const ai = tender.aiVagueDescription ? `${tender.aiVagueDescription}`.trim() : '';
        const desc = tender.description ? `${tender.description}`.trim() : '';
        let text = ai.length > 0 ? ai : desc;

        // Paid users should not see the subscription prompt embedded in vague description.
        const isPaid = !this.user.canAdHoc('notSubscribed');
        if (isPaid && text) {
            text = text.replace("*Subscribe to see buyer's tender documents in your account.", '').trim();
        }

        return this.displayOrNotProvided(text);
    }

    getDisplayLocation(value: string): string {
        if (value === null || value === undefined) {
            return value;
        }

        const source = `${value}`.trim();
        if (!source) {
            return source;
        }

        const selected = new Set(
            source
                .split(',')
                .map(x => x.trim())
                .filter(x => x.length > 0)
        );

        const hasAll = (required: string[]) => required.every(x => selected.has(x));
        if (hasAll(TenderResultsComponent.UK_LOCATIONS)) {
            return 'All UK';
        }
        if (hasAll(TenderResultsComponent.ENGLAND_LOCATIONS)) {
            return 'All England';
        }
        return source;
    }

    addNote() {

        var dto = new AddSubscriberNoticeNoteDto();
        dto.content = this.noteToAdd;
        dto.noticeId = this.selectedTender.id;

        if (this.showReminder && this.reminderDate != null) {
            var hours = this.reminderTimeHours < 10 ? `0${this.reminderTimeHours}` : this.reminderTimeHours.toString();
            var mins = this.reminderTimeMinutes < 10 ? `0${this.reminderTimeMinutes}` : this.reminderTimeMinutes.toString();

            var date = DateConverter.fromDateToServerString(this.reminderDate);
            date = `${date} ${hours}:${mins}`;

            dto.reminder = date;
            dto.reminderTime = `${hours}:${mins}`;
        }         

        this.tenderService.addNote(dto).subscribe(() => {
            const note = new TenderNote();
            note.content = this.noteToAdd;
            note.createdBy = this.user.email;
            const now = new Date();

            var day = now.getDate() < 10 ? `0${now.getDate()}` : `${now.getDate()}`;
            // < 9 for month since months are an array 0 - 11
            var month = now.getMonth() < 9 ? `0${now.getMonth() + 1}` : `${now.getMonth() + 1}`;

            var hour = now.getHours() < 10 ? `0${now.getHours()}` : `${now.getHours()}`;
            var min = now.getMinutes() < 10 ? `0${now.getMinutes()}` : `${now.getMinutes()}`;

            note.jsCreated = `${day}/${month}/${now.getFullYear()} ${hour}:${min}`;

            if (this.showReminder) {
                note.jsReminder = dto.reminder;
            }            

            this.selectedTender.notes.unshift(note);

            this.noteToAdd = null;
            this.addingNote = false;
            this.tabCounts.noteCount++;
        });
    }

    viewDocument(documentId: number) {
        this.tenderService.viewDocument(documentId).subscribe(() => {

            this.selectedTender.documents.forEach((document) => {
                if (document.id == documentId) {
                    document.jsLastViewed = new Date().toLocaleString();
                }                
            });
        });
    }

    viewZip(noticeId: number) {
        this.tenderService.viewZipDocument(noticeId).subscribe(() => {
            this.selectedTender.jsZipLastViewed = new Date().toLocaleString();
        });
    }

    acknowledgeDocuments() {
        this.acknowledgedDate = new Date().toLocaleString();
        this.accountService.setDocumentsAcknowledged().subscribe(() => {
            this.documentsAgreed = true;
        });
    }

    selectTab(tab: string, event?: Event) {
        if (event) {
            event.preventDefault();
        }
        this.isAssignOpen = false;
        this.activatedTab = tab;
    }

    openHvtTab(sectionNumber: number, event: Event) {
        event.preventDefault();
        this.isAssignOpen = false;
        this.activatedTab = `hvt-${sectionNumber}`;
        this.onOpenHvtSection(sectionNumber);
    }

    openSchemaTab(sectionNumber: number, event: Event) {
        event.preventDefault();
        this.isAssignOpen = false;
        this.activatedTab = `schema-${sectionNumber}`;
        this.onOpenSchema8Section(sectionNumber);
    }

    toggleAssign(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.isAssignOpen = !this.isAssignOpen;
        if (this.isAssignOpen) {
            setTimeout(() => {
                const el = document.getElementById('assignSelectTop') as HTMLSelectElement | null;
                if (el) {
                    el.focus();
                }
            }, 0);
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.isAssignOpen) {
            return;
        }
        const container = this.assignContainerRef ? this.assignContainerRef.nativeElement : null;
        const target = event.target as Node | null;
        if (!container || !target) {
            this.isAssignOpen = false;
            return;
        }
        if (!container.contains(target)) {
            this.isAssignOpen = false;
        }
    }

    isFreeUser(): boolean {
        return this.user != null && this.user.canAdHoc('notSubscribed');
    }

    showBuyersInformationTab(): boolean {
        if (!this.selectedTender) {
            return false;
        }
        if (this.isFreeUser()) {
            return this.hasBuyerContentFromList;
        }
        const t = this.selectedTender;
        const desc = t.buyersDescription && t.buyersDescription.trim().length > 0;
        const org = t.buyerOrganisationName && t.buyerOrganisationName.trim().length > 0;
        const contact = (t.contactName && t.contactName.trim().length > 0)
            || (t.contactEmail && t.contactEmail.trim().length > 0)
            || (t.contactNumber && t.contactNumber.trim().length > 0);
        const addr = t.contactAddress && (
            (t.contactAddress.line1 && t.contactAddress.line1.trim().length > 0)
            || (t.contactAddress.city && t.contactAddress.city.trim().length > 0)
            || (t.contactAddress.postcode && t.contactAddress.postcode.trim().length > 0));
        const link = (t.secureUrl && t.secureUrl.trim().length > 0) || (t.url && t.url.trim().length > 0);
        return desc || org || contact || addr || link;
    }

    get viewNoticeHref(): string | null {
        if (!this.selectedTender) {
            return null;
        }
        const s = this.selectedTender.secureUrl;
        const u = this.selectedTender.url;
        if (s && s.trim().length > 0) {
            return s;
        }
        if (u && u.trim().length > 0) {
            return u;
        }
        return null;
    }

    whatToDoNext() {
        $('#next-modal').modal();
    }

    showNoteEditor() {
        this.addingNote = true;
    }

    cancelNewNote() {
        this.addingNote = false;
        this.noteToAdd = null;
    }

    addReminder() {
        if (this.reminderDate === null) {
            this.showReminderError = true;
            return;
        }

        this.showReminderError = false;

        var hours = this.reminderTimeHours < 10 ? `0${this.reminderTimeHours}` : this.reminderTimeHours.toString();
        var mins = this.reminderTimeMinutes < 10 ? `0${this.reminderTimeMinutes}` : this.reminderTimeMinutes.toString();

        var date = DateConverter.fromDateToServerString(this.reminderDate);
        date = `${date} ${hours}:${mins}`;

        this.tenderService.addReminder(this.selectedTender.id, date, this.reminderText).subscribe(() => {
            var day = this.reminderDate.date() < 10 ? `0${this.reminderDate.date()}` : this.reminderDate.date().toString();
            var month = this.reminderDate.month() < 9 ? `0${this.reminderDate.month() + 1}` : (this.reminderDate.month() + 1).toString();

            this.selectedTender.reminders.push({
                jsDue: `${day}/${month}/${this.reminderDate.year()} ${hours}:${mins}`,
                text: this.reminderText
            });

            this.selectedTender.reminders.sort((r1: TenderReminder, r2: TenderReminder) => {
                var r1Due = moment(r1.jsDue, 'DD/MM/YYYY HH:mm');
                var r2Due = moment(r2.jsDue, 'DD/MM/YYYY HH:mm');

                return r1Due > r2Due
                    ? 1
                    : r1Due < r2Due
                        ? -1
                        : 0;
            });

            this.reminderText = null;
            this.reminderDate = moment();
            this.reminderTimeHours = this.reminderDate.hours() + 1;
            this.reminderTimeMinutes = 0;
            this.addingReminder = false;
            this.tabCounts.reminderCount++;
        });
    }

    openExtrasTab(name: string) {
        this.extrasExpanded = true;
        this.extrasTab = name;
    }

    activeTab(name: string) {
        return this.extrasTab === name;
    }

    closeExtras() {
        this.extrasExpanded = false;
        this.extrasTab = null;
    }

    onSearchComplete() {
        document.getElementById('results-scroll').scrollTop = 0;
    }

    notTeamMember(): boolean {
        return (this.user.role === 'Supplier');
    }

    get remindersText(): string {
        return (this.tabCounts.reminderCount <= 0 ? '' : ` (${this.tabCounts.reminderCount})`);
    }

    get notesText(): string {
        return (this.tabCounts.noteCount <= 0 ? '' : ` (${this.tabCounts.noteCount})`);
    }

    showInfoDialog() {
        $('#info-modal').modal();
    }

    //scheduleAutoRefresh() {
    //    const source = Observable.timer(this.autoRefreshDelay);

    //    this.autoRefreshSub = source.subscribe(() => {
    //        if (this.autoRefreshSub) {
    //            this.autoRefreshSub.unsubscribe();
    //        };

    //        if (this.selectedTenderSummary) {
    //            this.showDetails(this.selectedTenderSummary);
    //        }

    //        this.scheduleAutoRefresh();
    //    });
    //}

    teamMember(): boolean {
        return (this.user.role != 'Supplier');
    }

    private canLoadAcknowledgementState(): boolean {
        return !!this.user && !this.user.canAdHoc('notSubscribed');
    }
}

export interface ITenderResultOptions {
    availableButtons: string[];
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/tenders/results.component.ts